import { yupResolver } from "@hookform/resolvers/yup";
import { designTokens } from "@itandi/itandi-bb-ui";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormControlText } from "@itandi/itandi-bb-ui/FormControlText";
import { FormGroup } from "@itandi/itandi-bb-ui/FormGroup";
import { Input } from "@itandi/itandi-bb-ui/Input";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { useId } from "react";
import { useForm } from "react-hook-form";
import useSWRMutation from "swr/mutation";
import type { VendorCompanyCreateParams } from "~/models/VendorCompany";
import { vendorCompanyCreateValidationSchema } from "~/models/VendorCompany";
import { create } from "~/utils/api/admin/VendorCompany";
import { isSuccess } from "~/utils/api/api_handler";
import { generatePath } from "~/utils/generatePath";
import { PASSWORD_RULE_HELP_TEXT } from "~/utils/PasswordValidationUtil";

type Props = {
	onClose: () => void;
};

export function VendorCompanyModal({ onClose }: Props) {
	const { notify } = useNotification();
	const formId = useId();

	const {
		register,
		handleSubmit,
		formState: { errors }
	} = useForm<VendorCompanyCreateParams>({
		defaultValues: {
			address: "",
			ceoFirstName: "",
			ceoLastName: "",
			loginEmail: "",
			name: "",
			password: "",
			postalCode: ""
		},
		mode: "onSubmit",
		resolver: yupResolver(vendorCompanyCreateValidationSchema)
	});

	const { trigger, isMutating } = useSWRMutation(
		generatePath("/admin/vendor_companies"),
		async (_key: string, { arg }: { arg: VendorCompanyCreateParams }) =>
			create(arg)
	);

	const onSubmit = handleSubmit(async (submitValue) => {
		const result = await trigger(submitValue);
		if (isSuccess(result)) {
			notify({ content: "作成しました。", variant: "success" });
			onClose();
		} else {
			notify({
				content: result.message ?? "エラーが発生しました。",
				variant: "error"
			});
		}
	});

	return (
		<Modalv2 isOpen onClose={onClose}>
			<ModalHeader title="施工会社の追加" />
			<ModalBody>
				<form id={formId} noValidate onSubmit={onSubmit}>
					<FormGroup variant="vertical">
						<FormControl
							chip="必須"
							isInvalid={!!errors.name}
							label="施工会社名"
						>
							<Input {...register("name")} />
							<FormControlText error={errors.name?.message} />
						</FormControl>
						<FormControl
							chip="必須"
							isInvalid={!!errors.loginEmail}
							label="ログインメールアドレス"
						>
							<Input {...register("loginEmail")} type="email" />
							<FormControlText error={errors.loginEmail?.message} />
						</FormControl>
						<FormControl
							chip="必須"
							isInvalid={!!errors.password}
							label="初期パスワード"
						>
							<Input
								{...register("password")}
								autoComplete="new-password"
								type="password"
							/>
							<FormControlText
								error={errors.password?.message}
								help={PASSWORD_RULE_HELP_TEXT}
							/>
						</FormControl>
						<FormControl
							chip="必須"
							isInvalid={!!(errors.ceoLastName ?? errors.ceoFirstName)}
							label="代表者氏名"
						>
							<Flex gap={designTokens["spacing/xs"]}>
								<Input {...register("ceoLastName")} placeholder="例）内装" />
								<Input {...register("ceoFirstName")} placeholder="例）太郎" />
							</Flex>
							<FormControlText
								error={
									errors.ceoLastName?.message ?? errors.ceoFirstName?.message
								}
							/>
						</FormControl>
						<FormControl
							chip="必須"
							isInvalid={!!errors.postalCode}
							label="郵便番号"
						>
							<Input placeholder="例）1234567" {...register("postalCode")} />
							<FormControlText error={errors.postalCode?.message} />
						</FormControl>
						<FormControl chip="必須" isInvalid={!!errors.address} label="住所">
							<Input
								placeholder="東京都港区六本木3-2-1 住友不動産六本木グランドタワー42F"
								{...register("address")}
							/>
							<FormControlText error={errors.address?.message} />
						</FormControl>
					</FormGroup>
				</form>
			</ModalBody>
			<ModalFooter
				leftArea={
					<Button
						disabled={isMutating}
						onClick={onClose}
						type="button"
						variant="secondary"
					>
						キャンセル
					</Button>
				}
				rightArea={
					<Button
						form={formId}
						loading={isMutating}
						type="submit"
						variant="primary"
					>
						追加
					</Button>
				}
			/>
		</Modalv2>
	);
}
